import React, { useState } from 'react';
import { QRGenerationOptions } from '../utils/qrGenerator';
import LogoUpload from './LogoUpload';
import { UploadedLogo } from '../services/logoService';

interface QRStyleEditorProps {
  options: QRGenerationOptions;
  onChange: (options: QRGenerationOptions) => void;
  className?: string;
}

type EditorTab = 'colors' | 'logo' | 'layout';

const QRStyleEditor: React.FC<QRStyleEditorProps> = ({ options, onChange, className = '' }) => {
  const [activeTab, setActiveTab] = useState<EditorTab>('colors');
  const [uploadError, setUploadError] = useState('');

  const updateOptions = (updates: Partial<QRGenerationOptions>) => onChange({ ...options, ...updates });
  const updateColor = (type: 'dark' | 'light', color: string) => updateOptions({ color: { ...options.color, [type]: color } });
  const updateLogo = (updates: Partial<NonNullable<QRGenerationOptions['logo']>>) => updateOptions({ logo: { enabled: false, ...options.logo, ...updates } });
  const updateStyle = (updates: Partial<NonNullable<QRGenerationOptions['style']>>) => updateOptions({ style: { ...options.style, ...updates } });

  const handleLogoUploaded = (uploadedLogo: UploadedLogo) => {
    setUploadError('');
    updateLogo({ enabled: true, url: uploadedLogo.url, uploadedLogoId: uploadedLogo.id, uploadedLogoName: uploadedLogo.name });
  };

  const tabs: Array<{ id: EditorTab; label: string; icon: React.ReactNode }> = [
    { id: 'colors', label: 'Colors', icon: <><circle cx="8" cy="8" r="3"/><circle cx="16" cy="16" r="3"/><path d="m10 10 4 4"/></> },
    { id: 'logo', label: 'Logo', icon: <><rect x="3" y="4" width="18" height="16" rx="3"/><circle cx="9" cy="10" r="2"/><path d="m4 17 5-4 4 3 3-2 4 3"/></> },
    { id: 'layout', label: 'Quality', icon: <><path d="M4 7h16M7 4v6M4 17h16M17 14v6"/></> }
  ];

  return (
    <div className={className}>
      <div className="flex gap-1 border-b border-slate-200 bg-slate-50/70 px-4 pt-3 sm:px-6">
        {tabs.map((tab) => (
          <button key={tab.id} type="button" onClick={() => setActiveTab(tab.id)} className={`style-tab ${activeTab === tab.id ? 'is-active' : ''}`}>
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">{tab.icon}</svg>
            {tab.label}
          </button>
        ))}
      </div>

      <div className="p-5 sm:p-6">
        {activeTab === 'colors' && (
          <div className="grid gap-6 md:grid-cols-2">
            <ColorField label="QR foreground" description="The scannable modules and finder patterns." value={options.color?.dark || '#111827'} onChange={(value) => updateColor('dark', value)} />
            <ColorField label="Background" description="Keep strong contrast for reliable scanning." value={options.color?.light || '#FFFFFF'} onChange={(value) => updateColor('light', value)} disabled={options.style?.transparentBackground} />
            <label className="setting-toggle md:col-span-2">
              <div><span className="text-sm font-semibold text-slate-800">Transparent background</span><p className="mt-1 text-xs leading-5 text-slate-500">Removes every white background area from PNG and SVG exports, including the logo plate.</p></div>
              <input type="checkbox" className="sr-only" checked={options.style?.transparentBackground || false} onChange={(event) => updateStyle({ transparentBackground: event.target.checked })} />
              <span className={`toggle-track ${options.style?.transparentBackground ? 'is-active' : ''}`}><span /></span>
            </label>
          </div>
        )}

        {activeTab === 'logo' && (
          <div className="space-y-6">
            <label className="setting-toggle">
              <div><span className="text-sm font-semibold text-slate-800">Center logo</span><p className="mt-1 text-xs leading-5 text-slate-500">Error correction automatically switches to high.</p></div>
              <input type="checkbox" className="sr-only" checked={options.logo?.enabled || false} onChange={(event) => updateLogo({ enabled: event.target.checked })} />
              <span className={`toggle-track ${options.logo?.enabled ? 'is-active' : ''}`}><span /></span>
            </label>

            <div className="grid gap-6 lg:grid-cols-2">
              <div>
                <label className="app-label">Upload asset</label>
                <LogoUpload onLogoUploaded={handleLogoUploaded} onError={setUploadError} className="mt-2" />
                {uploadError && <p className="mt-2 text-xs font-medium text-red-600">{uploadError}</p>}
              </div>
              <div>
                <label htmlFor="logo-url" className="app-label">Or use an image URL</label>
                <input id="logo-url" type="url" value={options.logo?.url || ''} onChange={(event) => updateLogo({ enabled: Boolean(event.target.value), url: event.target.value })} placeholder="https://example.com/logo.png" className="app-input mt-2" />
                <p className="mt-2 text-xs leading-5 text-slate-500">Use a square PNG, JPG, WebP or SVG with enough padding.</p>
              </div>
            </div>

            {options.logo?.enabled && (
              <div className="grid gap-6 rounded-xl border border-slate-200 bg-slate-50 p-4 sm:grid-cols-2">
                <RangeField label="Logo size" value={options.logo?.size || 15} min={10} max={25} suffix="%" onChange={(value) => updateLogo({ size: value })} />
                <RangeField label="Corner radius" value={options.logo?.borderRadius || 8} min={0} max={24} suffix="px" onChange={(value) => updateLogo({ borderRadius: value })} />
                <ColorField label="Logo background" value={options.logo?.backgroundColor || '#FFFFFF'} onChange={(value) => updateLogo({ backgroundColor: value })} compact />
              </div>
            )}
          </div>
        )}

        {activeTab === 'layout' && (
          <div className="grid gap-6 md:grid-cols-2">
            <RangeField label="Export size" value={options.size || 256} min={160} max={1024} step={32} suffix="px" onChange={(value) => updateOptions({ size: value })} />
            <RangeField label="Quiet zone" value={options.margin ?? 2} min={0} max={8} suffix=" modules" onChange={(value) => updateOptions({ margin: value })} />
            <div>
              <label htmlFor="correction-level" className="app-label">Error correction</label>
              <select id="correction-level" value={options.errorCorrectionLevel || 'M'} onChange={(event) => updateOptions({ errorCorrectionLevel: event.target.value as QRGenerationOptions['errorCorrectionLevel'] })} disabled={options.logo?.enabled} className="app-input mt-2">
                <option value="L">Low — smallest pattern</option>
                <option value="M">Medium — recommended</option>
                <option value="Q">Quartile — more resilient</option>
                <option value="H">High — best with a logo</option>
              </select>
              <p className="mt-2 text-xs leading-5 text-slate-500">{options.logo?.enabled ? 'High correction is enforced while a logo is enabled.' : 'Higher levels create denser QR patterns.'}</p>
            </div>
            <div className="rounded-xl border border-blue-100 bg-blue-50 p-4 text-xs leading-5 text-blue-800"><strong>Tip:</strong> Keep a quiet zone of at least 2 modules and test the final code on multiple phones before printing.</div>
          </div>
        )}
      </div>
    </div>
  );
};

const ColorField: React.FC<{ label: string; description?: string; value: string; onChange: (value: string) => void; disabled?: boolean; compact?: boolean }> = ({ label, description, value, onChange, disabled, compact }) => (
  <div className={compact ? '' : 'min-w-0'}>
    <label className="app-label">{label}</label>
    <div className={`mt-2 flex items-center gap-2 rounded-xl border border-slate-200 bg-white p-2 ${disabled ? 'opacity-45' : ''}`}>
      <input type="color" value={value} onChange={(event) => onChange(event.target.value)} disabled={disabled} className="h-10 w-12 cursor-pointer rounded-lg border-0 bg-transparent p-0" />
      <input type="text" value={value.toUpperCase()} onChange={(event) => onChange(event.target.value)} disabled={disabled} className="min-w-0 flex-1 bg-transparent font-mono text-sm font-medium text-slate-700 outline-none" />
    </div>
    {description && <p className="mt-2 text-xs leading-5 text-slate-500">{description}</p>}
  </div>
);

const RangeField: React.FC<{ label: string; value: number; min: number; max: number; step?: number; suffix: string; onChange: (value: number) => void }> = ({ label, value, min, max, step = 1, suffix, onChange }) => (
  <div>
    <div className="flex items-center justify-between gap-3"><label className="app-label">{label}</label><span className="rounded-md bg-slate-100 px-2 py-1 font-mono text-xs font-semibold text-slate-600">{value}{suffix}</span></div>
    <input type="range" min={min} max={max} step={step} value={value} onChange={(event) => onChange(Number(event.target.value))} className="app-range mt-4 w-full" />
    <div className="mt-1 flex justify-between text-[10px] font-medium text-slate-400"><span>{min}</span><span>{max}</span></div>
  </div>
);

export default QRStyleEditor;
